import { useState, useCallback, useEffect, useRef } from 'react'

export type CopyStatus = 'idle' | 'copying' | 'success' | 'error'

export interface UseCopyToClipboardOptions {
  resetDelay?: number
  onSuccess?: (text: string) => void
  onError?: (error: Error) => void
}

export interface UseCopyToClipboardReturn {
  copy: (text: string) => Promise<boolean>
  status: CopyStatus
  isSupported: boolean
  reset: () => void
}

const isClipboardSupported = (): boolean => {
  return !!(
    navigator.clipboard &&
    typeof navigator.clipboard.writeText === 'function' &&
    window.isSecureContext
  )
}

const fallbackCopy = (text: string): boolean => {
  const textArea = document.createElement('textarea')
  textArea.value = text
  textArea.style.position = 'fixed'
  textArea.style.left = '-999999px'
  textArea.style.top = '-999999px'
  document.body.appendChild(textArea)
  textArea.focus()
  textArea.select()

  try {
    // execCommand is deprecated but still needed as a fallback
    // for older browsers that don't support the Clipboard API
    const successful = document.execCommand('copy')
    document.body.removeChild(textArea)
    return successful
  } catch (error) {
    document.body.removeChild(textArea)
    return false
  }
}

export const useCopyToClipboard = (
  options: UseCopyToClipboardOptions = {}
): UseCopyToClipboardReturn => {
  const { resetDelay = 2000, onSuccess, onError } = options
  const [status, setStatus] = useState<CopyStatus>('idle')
  const [isSupported] = useState(isClipboardSupported())
  const resetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const reset = useCallback(() => {
    setStatus('idle')
    if (resetTimeoutRef.current) {
      clearTimeout(resetTimeoutRef.current)
      resetTimeoutRef.current = null
    }
  }, [])

  const copy = useCallback(
    async (text: string): Promise<boolean> => {
      if (!text) {
        return false
      }

      reset()
      setStatus('copying')

      try {
        let successful = false

        if (isSupported) {
          await navigator.clipboard.writeText(text)
          successful = true
        } else {
          successful = fallbackCopy(text)
        }

        if (successful) {
          setStatus('success')
          onSuccess?.(text)

          resetTimeoutRef.current = setTimeout(() => {
            setStatus('idle')
          }, resetDelay)

          return true
        } else {
          throw new Error('Failed to copy text')
        }
      } catch (error) {
        setStatus('error')
        const errorObj =
          error instanceof Error ? error : new Error('Copy failed')
        onError?.(errorObj)

        resetTimeoutRef.current = setTimeout(() => {
          setStatus('idle')
        }, resetDelay)

        return false
      }
    },
    [isSupported, onSuccess, onError, resetDelay, reset]
  )

  useEffect(() => {
    return () => {
      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current)
      }
    }
  }, [])

  return {
    copy,
    status,
    isSupported,
    reset,
  }
}

export default useCopyToClipboard
